import {
  NotFoundException,
  UnAuthorizedException,
} from '../exceptions/fetchException';
import { GridusException } from '../exceptions/gridusException';
import { fetchToken, storeToken } from './cookie';

enum TokenType {
  IdToken,
  AccessToken,
  RefreshToken,
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL;

const createQueryString = (params: Record<string, string | number>): string => {
  const stringifiedParams: Record<string, string> = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, String(value)])
  );

  const queryString = new URLSearchParams(stringifiedParams).toString();
  return queryString ? `?${queryString}` : '';
};

const getTokenFromCookie = (tokenType: TokenType): string | null => {
  switch (tokenType) {
    case TokenType.IdToken:
      return fetchToken('idToken');
    case TokenType.AccessToken:
      return fetchToken('accessToken');
    case TokenType.RefreshToken:
      return fetchToken('refreshToken');
  }
};

const getFetchPromise = (
  path: string,
  options: RequestOptions = {},
  tokenType: TokenType = TokenType.IdToken
): Promise<Response> => {
  const { params, ...fetchOptions } = options;
  const queryString = params ? createQueryString(params) : '';
  const token = getTokenFromCookie(tokenType);

  return fetch(`${API_BASE_URL}${path}${queryString}`, {
    ...fetchOptions,
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    },
    cache: 'no-store',
  });
};

const refreshToken = async (
  path: string,
  options: RequestOptions = {},
  tokenType: TokenType = TokenType.IdToken
): Promise<Response> => {
  return getFetchPromise(
    '/auth/refresh',
    {
      ...options,
      method: 'POST',
    },
    TokenType.RefreshToken
  ).then(async (response) => {
    switch (response.status) {
      case 200: {
        const value = await response.json();
        if (value.status === 'error') throw new UnAuthorizedException();
        else {
          // Store tokens
          const { AuthenticationResult } = value.data;
          if (AuthenticationResult) {
            const { AccessToken, IdToken } = AuthenticationResult;

            if (!AccessToken || !IdToken) throw new UnAuthorizedException();

            // if authentication success set tokens in cookie
            storeToken('accessToken', AccessToken);
            storeToken('idToken', IdToken);
          }
        }

        return getFetchPromise(path, options, tokenType);
      }

      default:
        throw new UnAuthorizedException();
    }
  });
};

const fetchRequest = async <T>(
  path: string,
  options: RequestOptions = {},
  tokenType: TokenType = TokenType.IdToken
): Promise<GridusResponseType<T> | GridusResponseErrorType> => {
  try {
    const responsePromise = getFetchPromise(path, options, tokenType);
    let response = await responsePromise;

    // Handle error
    switch (response.status) {
      case 204:
        return { status: 'success' } as GridusResponseType<T>;
      case 401:
        response = await refreshToken(path, options, tokenType);
        break;
      case 404:
        throw new NotFoundException();
    }

    const responseData = await response.json();

    return responseData;
  } catch (error) {
    throw error instanceof GridusException
      ? error
      : new GridusException(
          error instanceof Error ? error.message : 'Unknown error'
        );
  }
};

const get = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<GridusResponseType<T> | GridusResponseErrorType> => {
  const data = await fetchRequest<T>(path, {
    method: 'GET',
    ...options,
  });
  return data;
};

const post = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<GridusResponseType<T> | GridusResponseErrorType> => {
  const data = await fetchRequest<T>(path, {
    method: 'POST',
    ...options,
  });
  return data;
};

const put = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<GridusResponseType<T> | GridusResponseErrorType> => {
  const data = await fetchRequest<T>(path, {
    method: 'PUT',
    ...options,
  });
  return data;
};

const patch = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<GridusResponseType<T> | GridusResponseErrorType> => {
  const data = await fetchRequest<T>(path, {
    method: 'PATCH',
    ...options,
  });
  return data;
};

const del = async <T>(
  path: string,
  options: RequestOptions = {}
): Promise<GridusResponseType<T> | GridusResponseErrorType> => {
  const data = await fetchRequest<T>(path, {
    method: 'DELETE',
    ...options,
  });
  return data;
};

export interface FetchDataArg {
  endpoint: string;
  options?: RequestOptions;
  fetch: typeof patch | typeof get | typeof post | typeof del | typeof put;
}

export { get, post, put, del, patch };
